import { useCallback, useState } from 'react';
import { templateService } from '../services/templateService';
import { normalizeTemplateHealth } from './useHealth';

export function getTemplateRowKey(template) {
  if (!template) return '';
  return `${template.name}::${template.bot_number || ''}`;
}

export default function useBatchTemplateHealth({ companyId } = {}) {
  const [healthMap, setHealthMap] = useState({});
  const [isCheckingAll, setIsCheckingAll] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const checkSingle = useCallback(
    async (template) => {
      if (!template) return null;
      const rowKey = getTemplateRowKey(template);
      const effectiveTemplateId =
        template.wa_Id ||
        template.wa_id ||
        template.template_id ||
        template.id ||
        template.name;

      setHealthMap((prev) => ({
        ...prev,
        [rowKey]: {
          ...prev[rowKey],
          loading: true,
          error: null,
        },
      }));

      try {
        const res = await templateService.getTrustSignalTemplateHealth({
          templateId: effectiveTemplateId,
          name: template.name,
          wabaNumber: template.bot_number || '',
          companyId: template.company_id || companyId || '',
        });
        const resData = res?.data ?? res;
        const normalized = normalizeTemplateHealth(resData);

        const record = {
          loading: false,
          data: normalized,
          qualityScore: normalized?.qualityScore || 'UNKNOWN',
          status: normalized?.status || template.status || 'APPROVED',
          rejectionReason: normalized?.rejectionReason || '',
          lastChecked: new Date(),
          error: null,
        };

        setHealthMap((prev) => ({
          ...prev,
          [rowKey]: record,
        }));
        return record;
      } catch (err) {
        const errMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Health check failed';

        const errorRecord = {
          loading: false,
          data: null,
          qualityScore: 'UNKNOWN',
          status: template.status || 'UNKNOWN',
          rejectionReason: '',
          lastChecked: new Date(),
          error: errMsg,
        };

        setHealthMap((prev) => ({
          ...prev,
          [rowKey]: errorRecord,
        }));
        return errorRecord;
      }
    },
    [companyId]
  );

  const checkBatch = useCallback(
    async (templatesList = [], concurrency = 3) => {
      if (!Array.isArray(templatesList) || templatesList.length === 0) return;

      setIsCheckingAll(true);
      setProgress({ current: 0, total: templatesList.length });

      let completed = 0;

      const worker = async (template) => {
        try {
          await checkSingle(template);
        } finally {
          completed += 1;
          setProgress({ current: completed, total: templatesList.length });
        }
      };

      const executing = new Set();
      for (const item of templatesList) {
        const p = Promise.resolve().then(() => worker(item));
        executing.add(p);
        const clean = () => executing.delete(p);
        p.then(clean, clean);
        if (executing.size >= concurrency) {
          await Promise.race(executing);
        }
      }
      await Promise.allSettled(Array.from(executing));

      setIsCheckingAll(false);
    },
    [checkSingle]
  );

  return {
    healthMap,
    isCheckingAll,
    progress,
    checkSingle,
    checkBatch,
    setHealthMap,
  };
}

